// ═══════════════════════════════════════════════════════════════
// HOOK: useVariableIncomes - FUNCIONAL SIN LOOP
// ✅ Usa useCallback para evitar loop infinito
// ✅ Usa el API existente de variableIncomesAPI
// ═══════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import variableIncomesAPI from '../api/variableIncomes';

export function useVariableIncomes() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * ✅ Obtener ingresos variables - MEMOIZADO
   */
  const getVariableIncomes = useCallback(async (employeeId, periodo, quincena) => {
    try {
      setLoading(true);
      setError(null);

      const data = await variableIncomesAPI.get(employeeId, periodo, quincena);
      return data;
    } catch (err) {
      // No loguear errores 404 (son normales cuando no hay datos)
      if (err.response?.status !== 404) {
        console.error('Error fetching variable incomes:', err);
        setError(err.response?.data?.error || err.message);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []); // ✅ Sin dependencias - la función nunca cambia

  /**
   * ✅ Guardar ingresos variables - MEMOIZADO
   */
  const saveVariableIncomes = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);

      // ✅ Obtener company_id desde múltiples fuentes
      const companyId = data.companyId 
        || data.company_id 
        || localStorage.getItem('company_id')
        || localStorage.getItem('companyId')
        || JSON.parse(localStorage.getItem('user') || '{}').company_id;

      if (!companyId) {
        throw new Error('company_id es requerido pero no se encontró');
      }

      // Transformar datos a formato esperado por API
      const payload = {
        employee_id: data.employeeId,
        periodo: data.periodo,
        quincena: data.quincena,
        company_id: companyId,
        payroll_id: data.payrollId || null,
        horas_extras: parseFloat(data.horas_extras || 0),
        comisiones: parseFloat(data.comisiones || 0),
        vacaciones: parseFloat(data.vacaciones || 0),
        incentivos: parseFloat(data.incentivos || 0),
        bonificaciones: parseFloat(data.bonificaciones || 0),
        otros_ingresos: parseFloat(data.otros_ingresos || 0),
        detalles: data.detalles || {}
      };

      console.log('✅ company_id encontrado:', companyId);

      const result = await variableIncomesAPI.upsert(payload);
      
      return {
        success: true,
        data: result
      };
    } catch (err) {
      console.error('Error saving variable incomes:', err);
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // ✅ Sin dependencias

  /**
   * ✅ Eliminar ingresos variables - MEMOIZADO
   */
  const deleteVariableIncomes = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);

      const result = await variableIncomesAPI.delete(id);
      
      return {
        success: true,
        data: result
      };
    } catch (err) {
      console.error('Error deleting variable incomes:', err);
      setError(err.response?.data?.error || err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // ✅ Sin dependencias

  /**
   * ✅ Obtener solo el total
   */
  const getTotal = useCallback(async (employeeId, periodo, quincena) => {
    try {
      setLoading(true);
      const total = await variableIncomesAPI.getTotal(employeeId, periodo, quincena);
      return total;
    } catch (err) {
      console.error('Error getting total:', err);
      return 0;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * ✅ Obtener breakdown completo
   */
  const getBreakdown = useCallback(async (employeeId, periodo, quincena) => {
    try {
      setLoading(true);
      const breakdown = await variableIncomesAPI.getBreakdown(employeeId, periodo, quincena);
      return breakdown;
    } catch (err) {
      console.error('Error getting breakdown:', err);
      return {
        horas_extras: 0,
        comisiones: 0,
        vacaciones: 0,
        incentivos: 0,
        bonificaciones: 0,
        otros_ingresos: 0,
        total_ingresos: 0,
        detalles: null
      };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getVariableIncomes,
    saveVariableIncomes,
    deleteVariableIncomes,
    getTotal,
    getBreakdown
  };
}

export default useVariableIncomes;
